/**
 * Shrinks a photograph before it is uploaded.
 *
 * A phone photo is 3-12 MB and 4000 pixels wide, which is far more than anyone needs to see a
 * scratch on a laptop lid - and six of them would blow straight through the server's request size
 * limit. Resizing to a long edge of 1600px as a JPEG keeps the detail that matters at a few hundred
 * KB, and the upload finishes on a site connection.
 *
 * Returns the original file untouched when it is already small, and falls back to it if the
 * browser cannot decode the image, so a resizing problem never blocks recording a check.
 */
const MAX_EDGE = 1600;
const SMALL_ENOUGH_BYTES = 800 * 1024;

export async function downscaleImage(file: File): Promise<File> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size <= SMALL_ENOUGH_BYTES) {
      bitmap.close();
      return file;
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.85),
    );
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' });
  } catch {
    return file;
  }
}
